import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle2, MessageCircle, Loader2, RefreshCcw } from 'lucide-react';
import { authAPI } from '@/services/authAPI';

/**
 * Reusable WhatsApp OTP verification block.
 * Two modes:
 *   - mode="signup": public flow; on success returns `phone_verification_token` via onVerified
 *   - mode="change-phone": authenticated flow; on success returns { phone } via onVerified
 *
 * The parent controls the phone value (`phone` prop) so the same input can be re-used
 * for signup, and disables the "Send OTP" button until the phone is well-formed.
 */
export default function WhatsAppPhoneVerify({
  phone,
  mode = 'signup',            // 'signup' | 'change-phone'
  disabled = false,
  onVerified,                 // (payload) => void
  onPhoneUnverified,          // () => void — parent should call when phone is edited after verify
  compact = false,
  testIdPrefix = 'wa-verify',
}) {
  const [stage, setStage] = useState('idle'); // idle | sent | verified
  const [otp, setOtp] = useState('');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [devOtp, setDevOtp] = useState('');    // only when delivery fails on preview
  const lastPhoneRef = useRef(phone);

  // If parent changes the phone number, force a fresh verification
  useEffect(() => {
    if (lastPhoneRef.current !== phone) {
      lastPhoneRef.current = phone;
      if (stage !== 'idle') {
        setStage('idle');
        setOtp('');
        setDevOtp('');
        setMsg('');
        setError('');
        if (onPhoneUnverified) onPhoneUnverified();
      }
    }
  }, [phone, stage, onPhoneUnverified]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const sendOtp = async () => {
    setBusy(true);
    setError('');
    setMsg('');
    setDevOtp('');
    try {
      const fn = mode === 'change-phone' ? authAPI.changePhoneSendOtp : authAPI.signupSendWhatsAppOtp;
      const r = await fn(phone);
      setStage('sent');
      setCooldown(60);
      if (r.otp_dev) {
        setDevOtp(r.otp_dev);
        setMsg(`WhatsApp delivery unavailable in preview. Use code: ${r.otp_dev}`);
      } else {
        setMsg('OTP sent to your WhatsApp. Check the chat from Numuv and enter the 6-digit code.');
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp || otp.length < 4) {
      setError('Enter the 6-digit OTP');
      return;
    }
    setBusy(true);
    setError('');
    try {
      if (mode === 'change-phone') {
        const r = await authAPI.changePhoneVerifyOtp(phone, otp);
        setStage('verified');
        setMsg('Phone number verified and updated.');
        onVerified && onVerified({ phone: r.phone });
      } else {
        const r = await authAPI.signupVerifyWhatsAppOtp(phone, otp);
        setStage('verified');
        setMsg('Phone verified. You can complete signup now.');
        onVerified && onVerified({ phone, phone_verification_token: r.phone_verification_token });
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid OTP');
    } finally {
      setBusy(false);
    }
  };

  const verified = stage === 'verified';
  const containerCls = compact
    ? 'mt-1'
    : 'mt-2 p-3 rounded-lg border border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-[#0a0a0b]';

  if (verified) {
    return (
      <div className={containerCls}>
        <div className="flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400" data-testid={`${testIdPrefix}-verified-badge`}>
          <CheckCircle2 className="w-4 h-4" /> WhatsApp phone verified
        </div>
      </div>
    );
  }

  return (
    <div className={containerCls}>
      {stage === 'idle' && (
        <button
          type="button"
          onClick={sendOtp}
          disabled={disabled || busy || !phone || phone.length < 8}
          data-testid={`${testIdPrefix}-send-btn`}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#25D366]/15 border border-[#25D366]/40 text-emerald-700 dark:text-[#25D366] hover:bg-[#25D366]/25 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageCircle className="w-3.5 h-3.5" />}
          Verify via WhatsApp
        </button>
      )}
      {stage === 'sent' && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <input
              value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              placeholder="6-digit code"
              data-testid={`${testIdPrefix}-otp-input`}
              className="flex-1 min-w-0 bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-emerald-500/40"
            />
            <button
              type="button"
              onClick={verifyOtp}
              disabled={busy || otp.length < 4}
              data-testid={`${testIdPrefix}-verify-btn`}
              className="px-3 py-2 rounded-lg text-xs font-medium bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50"
            >
              {busy ? '...' : 'Verify'}
            </button>
          </div>
          <button
            type="button"
            onClick={sendOtp}
            disabled={cooldown > 0 || busy}
            data-testid={`${testIdPrefix}-resend-btn`}
            className="inline-flex items-center gap-1 text-[11px] text-slate-500 dark:text-zinc-500 hover:text-emerald-600 disabled:opacity-50"
          >
            <RefreshCcw className="w-3 h-3" />
            {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend OTP'}
          </button>
        </div>
      )}
      {msg && !error && <p className="text-[11px] text-emerald-600 dark:text-emerald-400 mt-1.5" data-testid={`${testIdPrefix}-msg`}>{msg}</p>}
      {error && <p className="text-[11px] text-red-600 dark:text-red-400 mt-1.5" data-testid={`${testIdPrefix}-error`}>{error}</p>}
      {devOtp && <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">Dev-only OTP shown because WhatsApp couldn&apos;t deliver.</p>}
    </div>
  );
}
